import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/authService';
import { setAuthFailureHandler, setCoHostRevokedHandler } from '@/services/apiService';
import { isCoHostUser, type AppAuthResponse, type AppLoginRequest, type AppMeResponse } from '@/types/auth';
import {
  getAccessToken,
  hasSessionHint,
  setAccessTokenCookie,
  setSessionHint,
  setCommunityUnverifiedFlag,
  setLogoutFlag,
  clearLogoutFlag,
} from '@/utils/auth';
import { getDefaultHomePath } from '@/utils/redirectByRole';
import { validateReturnUrl } from '@/utils/returnUrl';
import { isNativePlatform } from '@/utils/capacitor';
import { openExternalUrl } from '@/utils/openExternalUrl';
import showToast from '@/components/ui/Toast';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

interface AuthState {
  user: AppMeResponse | null;
  auth: AppAuthResponse | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: (payload: AppLoginRequest, returnUrl?: string | null) => Promise<void>;
  startSession: (data: AppAuthResponse, returnUrl?: string | null) => Promise<void>;
  logout: () => Promise<void>;
  enterCommunity: (communityId: number) => Promise<void>;
  exitCommunity: () => Promise<void>;
  isCoHost: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<AppMeResponse | null>(null);
  const [auth, setAuth] = useState<AppAuthResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const handlingRevokeRef = useRef(false);

  const applySession = useCallback((authData: AppAuthResponse, meData: AppMeResponse) => {
    setAuth(authData);
    setUser(meData);
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem('sporto_role', meData.role);
    if (meData.role === 'community') {
      const commName = meData.name || meData.community_user?.name || '';
      const commAvatar = meData.avatar || meData.community_user?.avatar || '';
      const commSlug = meData.slug || meData.community_user?.nickname || '';
      if (commName) localStorage.setItem('communityName', commName);
      if (commAvatar) localStorage.setItem('communityAvatar', commAvatar);
      if (commSlug) localStorage.setItem('communityNickname', commSlug);
    } else {
      ['communityName', 'communityAvatar', 'communityNickname'].forEach((key) => {
        localStorage.removeItem(key);
      });
      if (meData.name) localStorage.setItem('clientName', meData.name);
      if (meData.avatar) localStorage.setItem('clientAvatar', meData.avatar);
    }
    window.dispatchEvent(new Event('localStorageUserUpdate'));
  }, []);

  const handleAuthFailure = useCallback(() => {
    authService.clearSession();
    setUser(null);
    setAuth(null);
    navigate('/', { replace: true });
  }, [navigate]);

  useEffect(() => {
    setAuthFailureHandler(handleAuthFailure);
  }, [handleAuthFailure]);

  useEffect(() => {
    const restoreSession = async () => {
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const ssoToken = urlParams.get('token');
        if (ssoToken) {
          setAccessTokenCookie(ssoToken, Math.floor(Date.now() / 1000) + 7 * 24 * 3600);
          setSessionHint();
          urlParams.delete('token');
          const newSearch = urlParams.toString();
          const newUrl =
            window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
          window.history.replaceState(null, '', newUrl);
        }
      }

      if (!hasSessionHint()) {
        setIsLoading(false);
        return;
      }
      try {
        const token = getAccessToken();
        let authData: AppAuthResponse;
        if (token) {
          try {
            authData = await authService.refresh();
          } catch {
            const me = await authService.me();
            authData = {
              token,
              exp: Math.floor(Date.now() / 1000) + 7 * 24 * 3600,
              role: me.role,
              user_id: me.user_id,
              email: me.email,
              username: me.username || '',
              name: me.name || '',
              avatar: me.avatar,
              slug: me.slug,
              community_user: me.community_user,
              acting_as: me.acting_as,
              managed_community_id: me.managed_community_id,
            };
          }
        } else {
          authData = await authService.refresh();
        }
        const meData = await authService.me();
        applySession(
          {
            ...authData,
            acting_as: meData.acting_as ?? authData.acting_as,
            managed_community_id: meData.managed_community_id ?? authData.managed_community_id,
          },
          meData
        );
        if (meData.role === 'community' && meData.is_verified === false) {
          setCommunityUnverifiedFlag(true);
        }
      } catch {
        authService.clearSession();
        setUser(null);
        setAuth(null);
      } finally {
        setIsLoading(false);
      }
    };
    restoreSession();
  }, [applySession]);

  const startSession = useCallback(
    async (data: AppAuthResponse, returnUrl?: string | null) => {
      const meData = await authService.me();
      applySession(data, meData);

      if (data.role === 'community' && data.is_verified === false) {
        setCommunityUnverifiedFlag(true);
      } else {
        setCommunityUnverifiedFlag(false);
      }

      const destination =
        validateReturnUrl(returnUrl ?? null, data.role) ??
        getDefaultHomePath(data.role, data.slug);
      clearLogoutFlag();
      if (destination.startsWith('http://') || destination.startsWith('https://')) {
        if (isNativePlatform()) {
          void openExternalUrl(destination);
          navigate(getDefaultHomePath(data.role, data.slug), { replace: true });
        } else {
          window.location.href = destination;
        }
      } else {
        navigate(destination);
      }
    },
    [navigate, applySession]
  );

  const login = useCallback(
    async (payload: AppLoginRequest, returnUrl?: string | null) => {
      const data = await authService.login(payload);
      await startSession(data, returnUrl);
    },
    [startSession]
  );

  const logout = useCallback(async () => {
    setLogoutFlag();
    try {
      await authService.logout();
    } catch {
      authService.clearSession();
    }
    clearLegacyCommunityStorage();
    setUser(null);
    setAuth(null);
    setCommunityUnverifiedFlag(false);
    navigate('/', { replace: true });
  }, [navigate]);

  const enterCommunity = useCallback(
    async (communityId: number) => {
      const data = await authService.enterCommunity(communityId);
      const meData = await authService.me();
      applySession(data, meData);
      setCommunityUnverifiedFlag(false);
    },
    [applySession]
  );

  const exitCommunity = useCallback(async () => {
    const data = await authService.exitCommunity();
    const meData = await authService.me();
    applySession(data, meData);
    setCommunityUnverifiedFlag(false);
  }, [applySession]);

  const handleCoHostRevoked = useCallback(
    async (message: string) => {
      if (handlingRevokeRef.current) return;
      handlingRevokeRef.current = true;
      showToast.error(message);
      try {
        try {
          await exitCommunity();
        } catch (firstError) {
          try {
            await exitCommunity();
          } catch (secondError) {
            showToast.error(
              getApiErrorMessage(secondError, getApiErrorMessage(firstError, message))
            );
            await logout();
            return;
          }
        }
        navigate('/', { replace: true });
      } finally {
        handlingRevokeRef.current = false;
      }
    },
    [exitCommunity, logout, navigate]
  );

  useEffect(() => {
    setCoHostRevokedHandler((message) => {
      void handleCoHostRevoked(message);
    });
  }, [handleCoHostRevoked]);

  const isCoHost = isCoHostUser(user) || isCoHostUser(auth);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      auth,
      isLoading,
      isAuthenticated: !!user,
      login,
      startSession,
      logout,
      enterCommunity,
      exitCommunity,
      isCoHost,
    }),
    [user, auth, isLoading, login, startSession, logout, enterCommunity, exitCommunity, isCoHost]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};

function clearLegacyCommunityStorage(): void {
  if (typeof localStorage === 'undefined') return;
  ['communityName', 'communityAvatar', 'community_user', 'emailVerified'].forEach((key) => {
    localStorage.removeItem(key);
  });
}
